import {
  useMergedShopTests,
  AllTestKitInfo,
  useShopifyMultipass,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { Card } from '@/components/design-system/Card';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@/components/design-system/Skeleton';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { EventTracker } from '@/utils';
import { analyticsEvent } from '@utils/analyticsEvent.ts';
import { ShopUnavailablePage } from '@screens/ShopPage/components/ShopUnavailablePage.tsx';
import { TestCard } from '@screens/ShopPage/components/TestCard';
import type { TestPurchaseOption } from '@screens/ShopPage/components/TestPurchaseOptionSelector';

const ShopItemSkeleton = () => (
  <Card className="overflow-hidden mb-6">
    <div className="px-5 pt-6">
      <div className="pb-6 flex flex-col-reverse md:flex-row items-center md:items-start">
        <Skeleton className="h-8 w-40 rounded" />
        <Skeleton className="h-6 w-48 mt-1 sm:mt-0 ml-auto" />
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between items-center">
        <div>
          <Skeleton className="h-8 w-60 mb-4" />
          <Skeleton className="h-8 w-24 mb-4" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Skeleton className="w-48 h-40 mt-4 md:mt-0" />
      </div>
    </div>

    <div className="px-5 py-6">
      <Skeleton className="h-24 w-full mb-6" />
      <div className="flex gap-4 w-full">
        <Skeleton className="flex-1 h-14 rounded-3xl" />
      </div>
    </div>
  </Card>
);

export const ShopTestsPage = () => {
  const { data, isLoading, isError } = useMergedShopTests();
  const { mutate: generateMultipassUrl, isPending: isMultipassLoading } =
    useShopifyMultipass();
  const { toast } = useToast();
  const [processingTestId, setProcessingTestId] = useState<string | null>(null);

  const handleCtaClick = (
    test: AllTestKitInfo,
    selectedOption: TestPurchaseOption = 'onetime',
  ) => {
    // Only one checkout can be in flight: a second click would open another
    // blank tab and race the shared processingTestId state.
    if (isMultipassLoading || processingTestId) return;

    if (!test.testId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
      });
      console.error('Test ID not found for cart operation.');
      return;
    }
    let finalShopifyCartUrl =
      selectedOption === 'subscribe'
        ? test?.subscription_cart_url
        : test?.shopify_cart_url;
    if (!finalShopifyCartUrl) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shopify cart URL not found.',
      });
      return;
    }
    const couponCodeValue = test?.couponCode || 'NOPROMO';
    const separator = finalShopifyCartUrl.includes('?') ? '&' : '?';
    finalShopifyCartUrl += `${separator}discount=${couponCodeValue}`;
    setProcessingTestId(test.testId);

    // Safari/iOS only honor `window.open` when invoked synchronously inside a
    // user gesture. Opening here (not in the async `onSuccess`) preserves the
    // user-activation so the popup is not blocked; we then redirect the tab
    // once the multipass URL is available.
    const checkoutTab = window.open('', '_blank');

    generateMultipassUrl(finalShopifyCartUrl, {
      onSuccess: (multipassData) => {
        setProcessingTestId(null);
        if (multipassData) {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.location.href = multipassData;
          } else {
            window.location.href = multipassData;
          }
        } else {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.close();
          }
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not retrieve checkout URL. Please try again.',
          });
        }
      },
      onError: (error: Error) => {
        setProcessingTestId(null);
        if (checkoutTab && !checkoutTab.closed) {
          checkoutTab.close();
        }
        toast({
          variant: 'destructive',
          title: 'Error',
          description:
            error.message || 'Could not proceed to checkout. Please try again.',
        });
      },
    });
    if (test?.testId === '32624267690020') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_GI_ORDERCTA);
    } else if (test?.testId === '40443528183844') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_OHI_ORDERCTA);
    } else if (test?.testId === '39953050566692') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_FBI_ORDERCTA);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <ShopItemSkeleton />
        <ShopItemSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  if (!data?.length) {
    return (
      <ShopUnavailablePage tab='tests' />
    )
  }

  const hasActiveTestSubscription = !!data?.some((test) => test.is_subscribed);

  return (
    <div className="space-y-8">
      {data?.map((test) => (
        <TestCard
          key={test.testId}
          test={test}
          isProcessing={isMultipassLoading && processingTestId === test.testId}
          isAnyOrderProcessing={isMultipassLoading || !!processingTestId}
          hasActiveTestSubscription={hasActiveTestSubscription}
          onOrder={handleCtaClick}
        />
      ))}
    </div>
  );
};
